import { updateNestedRow } from '@/functions/nested';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faSpinnerThird } from '@fortawesome/sharp-solid-svg-icons';
import React, { useCallback } from 'react';
import './NestedTable.css';

// row buttons

// Define the type for our table data
export interface TableRow {
  [key: string]: any;
}

// Table column definition
interface TableColumn {
  header: string;
  accessor: string;
}

export interface RowAction {
  Name: string;
  OnClick: any;
  Icon: IconDefinition;
  Type?: 'danger';
}

interface SCNestedTableProps {
  columns: TableColumn[];
  idField: string;
  subRowsField: string;
  data: TableRow[];
  rowActions?: RowAction[];
  label?: string;
  emptyLabel: string;
  loadingMessage?: string;
  isLoading?: boolean;
  loadingRowId?: string;
  emptyPrompts?: { Label: string; OnClick: () => void }[];
  headerButtons?: { Icon: IconDefinition; Label: string; Disabled?: boolean; OnClick: any }[];
  maxHeight?: number | null;
  expandedIcon?: IconDefinition;
  collapsedIcon?: IconDefinition;
  updateData: (data: any[]) => void;
  onRowClick?: (row: TableRow) => void;
  rowActionRenderer?: (row: any) => RowAction[];
}

export function SCNestedTable({
  columns,
  data,
  rowActions,
  onRowClick,
  idField,
  subRowsField,
  label,
  emptyLabel,
  emptyPrompts,
  headerButtons,
  rowActionRenderer,
  expandedIcon = faChevronDown,
  collapsedIcon = faChevronRight,
  updateData,
  isLoading,
  loadingRowId,
}: SCNestedTableProps) {
  const toggleRowExpansion = useCallback(
    (row: any) => {
      const newData = updateNestedRow(
        JSON.parse(JSON.stringify(data)),
        idField,
        subRowsField,
        row[idField],
        { isExpanded: !(row.isExpanded || false) },
      );
      updateData(newData);
    },
    [data],
  );

  const handleRowClick = useCallback((row: TableRow) => {
    onRowClick?.(row);
  }, []);

  const getRowActionClassName = useCallback((type) => {
    if (type == 'danger') {
      return 'eone-ui-row-button-danger';
    } else {
      return 'eone-ui-table-row-button';
    }
  }, []);

  const getRowActions = (row: TableRow) => {
    if (rowActions && rowActions.length > 0) {
      return (
        <td className="eone-ui-table-row-data">
          <div className="nested-table-cell-row-action-container">
            {rowActions.map((action) => (
              <div
                className={getRowActionClassName(action.Type)}
                key={action.Name}
                title={action.Name}
                onClick={(e) => {
                  e.preventDefault();
                  action.OnClick(row[idField]);
                }}
              >
                <FontAwesomeIcon icon={action.Icon} />
              </div>
            ))}
          </div>
        </td>
      );
    } else if (rowActionRenderer) {
      const actions = rowActionRenderer(row);
      return (
        <td className="eone-ui-table-row-data">
          <div className="nested-table-cell-row-action-container">
            {actions.map((action) => (
              <div
                className={getRowActionClassName(action.Type)}
                key={action.Name}
                title={action.Name}
                onClick={(e) => {
                  e.preventDefault();
                  action.OnClick(row[idField]);
                }}
              >
                <FontAwesomeIcon icon={action.Icon} />
              </div>
            ))}
          </div>
        </td>
      );
    } else {
      return null;
    }
  };

  // Recursive row rendering
  const renderRow = (row: TableRow, depth: number = 0) => {
    const hasSubRows = row[subRowsField] && row[subRowsField].length > 0;
    const isExpanded = row.isExpanded;

    return (
      <React.Fragment key={row[idField]}>
        <tr
          className={`eone-ui-table-row ${hasSubRows ? 'has-subrows' : ''}`}
          onClick={() => handleRowClick(row)}
        >
          {columns.map((column, colIndex) => (
            <td
              key={column.accessor}
              className="eone-ui-table-row-data"
              style={{ paddingLeft: colIndex === 0 ? `${depth * 20 + 10}px` : undefined }}
            >
              <div className="nested-table-row-data">
                {colIndex === 0 && isLoading && loadingRowId == row[idField] && (
                  <FontAwesomeIcon
                    icon={faSpinnerThird}
                    spin
                    style={{ position: 'relative', marginRight: 10 }}
                  />
                )}
                {colIndex === 0 && hasSubRows && (
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      toggleRowExpansion(row);
                    }}
                    className="nested-table-expand-btn"
                  >
                    {isExpanded ? (
                      <FontAwesomeIcon icon={expandedIcon} />
                    ) : (
                      <FontAwesomeIcon icon={collapsedIcon} />
                    )}
                  </div>
                )}
                {row[column.accessor]}
              </div>
            </td>
          ))}

          {getRowActions(row)}
        </tr>

        {/* Render subrows if expanded */}
        {hasSubRows &&
          isExpanded &&
          row[subRowsField]?.map((subRow) => renderRow(subRow, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className="nested-table-container">
      <div className="nested-table-label-container">
        {label && <p className="nested-table-label">{label}</p>}
        {headerButtons && (
          <div className="nested-table-header-buttons-container">
            {headerButtons.map((btn) => (
              <div
                onClick={btn.Disabled ? null : btn.OnClick}
                className={`nested-table-header-button-container${btn.Disabled ? '-disabled' : ''}`}
                key={btn.Label}
              >
                <FontAwesomeIcon icon={btn.Icon} />
                <p>{btn.Label}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <table className="nested-table">
        <thead>
          <tr className="eone-ui-table-row-header">
            {columns.map((column) => (
              <th key={column.accessor} className="eone-ui-table-header-data">
                <div className="eone-ui-table-header-container">
                  <a className="eone-ui-table-header-sort-link" title="Click to sort">
                    <span className="eone-ui-table-header-sorted">{column.header}</span>
                  </a>
                </div>
              </th>
            ))}
            {((rowActions && rowActions.length > 0) || rowActionRenderer) && (
              <th className="eone-ui-table-header-data" />
            )}
          </tr>
        </thead>
        <tbody>{data.map((row) => renderRow(row))}</tbody>
      </table>
      {isLoading == false && data.length == 0 && (
        <div className="nested-table-body">
          <p style={{ margin: 0 }}>{emptyLabel}</p>
          {emptyPrompts &&
            emptyPrompts.map((prompt) => (
              <div style={{ cursor: 'pointer' }} onClick={prompt.OnClick} key={prompt.Label}>
                <p className="empty-prompt-label">{prompt.Label}</p>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
